import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import type { User } from '@pf/shared';

// The /api/users/me response augments the user record with an `isStaff` flag
// computed server-side from the STAFF_EMAILS allowlist (see api lib/admin-auth.ts).
// Used to gate the Admin nav link / route. The API still enforces staff access
// on every /api/admin/* route, so this is purely a UI affordance.
export interface MeResponse {
  data: User & { isStaff: boolean };
}

const QUERY_KEY = ['me'];

export function useMe() {
  return useQuery<MeResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => apiGet<MeResponse>('/users/me'),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Convenience hook returning whether the current user is platform staff.
 * Defaults to false while loading or on error, so the admin UI stays hidden
 * unless the server explicitly confirms staff status.
 */
export function useIsStaff(): boolean {
  const { data } = useMe();
  return data?.data.isStaff ?? false;
}
